import { DocumentType, isFileDocumentType } from '@/models/DocumentType';

/**
 * Calculate expiration date based on document type settings
 */
export const calculateExpirationDate = (documentType: DocumentType, creationDate: Date = new Date()): Date | null => {
  // Only file-based document types have expiration settings
  if (!isFileDocumentType(documentType) || !documentType.default_expiration_type) {
    return null;
  }

  switch (documentType.default_expiration_type) {
    case 'days':
      // Use default_expiration_days if available
      if (documentType.default_expiration_days) {
        const expirationDate = new Date(creationDate);
        expirationDate.setDate(expirationDate.getDate() + documentType.default_expiration_days);
        return expirationDate;
      }
      return null;

    case 'date':
      // Specific date (day/month/year)
      if (documentType.default_expiration_day && documentType.default_expiration_month) {
        const year = documentType.default_expiration_year || new Date().getFullYear();
        const expirationDate = new Date(year, documentType.default_expiration_month - 1, documentType.default_expiration_day);
        
        // If the date has already passed this year and no specific year is set, use next year
        if (!documentType.default_expiration_year && expirationDate < creationDate) {
          expirationDate.setFullYear(year + 1);
        }
        
        return expirationDate;
      }
      return null;

    case 'annual':
      // Annual expiration on the same date each year
      if (documentType.default_expiration_day && documentType.default_expiration_month) {
        const currentYear = creationDate.getFullYear();
        const expirationDate = new Date(currentYear, documentType.default_expiration_month - 1, documentType.default_expiration_day);
        
        // If the date has already passed this year, use next year
        if (expirationDate < creationDate) {
          expirationDate.setFullYear(currentYear + 1);
        }
        
        return expirationDate;
      }
      return null;

    default:
      return null;
  }
};

/**
 * Format expiration settings for display
 */
export const formatExpirationSettings = (documentType: DocumentType): string => {
  // Only file-based document types have expiration settings
  if (!isFileDocumentType(documentType) || !documentType.default_expiration_type) {
    return 'No default expiration';
  }

  switch (documentType.default_expiration_type) {
    case 'days':
      return documentType.default_expiration_days 
        ? `${documentType.default_expiration_days} days from creation`
        : 'No expiration';

    case 'date':
      if (documentType.default_expiration_day && documentType.default_expiration_month) {
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const month = monthNames[documentType.default_expiration_month - 1];
        const year = documentType.default_expiration_year || 'current year';
        return `${month} ${documentType.default_expiration_day}, ${year}`;
      }
      return 'Invalid date configuration';

    case 'annual':
      if (documentType.default_expiration_day && documentType.default_expiration_month) {
        const monthNames = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const month = monthNames[documentType.default_expiration_month - 1];
        return `${month} ${documentType.default_expiration_day} (annually)`;
      }
      return 'Invalid annual configuration';

    default:
      return 'Unknown expiration type';
  }
};

/**
 * Validate expiration settings
 */
export const validateExpirationSettings = (
  type: string,
  day?: number,
  month?: number,
  year?: number
): { isValid: boolean; error?: string } => {
  if (type === 'days') {
    return { isValid: true };
  }

  if (type === 'date' || type === 'annual') {
    if (!day || !month) {
      return { isValid: false, error: 'Day and month are required for date/annual expiration' };
    }

    if (day < 1 || day > 31) {
      return { isValid: false, error: 'Day must be between 1 and 31' };
    }

    if (month < 1 || month > 12) {
      return { isValid: false, error: 'Month must be between 1 and 12' };
    }

    // Check for valid day in the specified month
    const daysInMonth = new Date(year || new Date().getFullYear(), month, 0).getDate();
    if (day > daysInMonth) {
      return { isValid: false, error: `Day ${day} is not valid for the selected month` };
    }

    if (type === 'date' && year && year < new Date().getFullYear()) {
      return { isValid: false, error: 'Year cannot be in the past' };
    }
  }

  return { isValid: true };
}; 